/**
 * Issuing a sales invoice from the app.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * `issueDocument(tx, params)` takes a live transaction, which is right for the
 * service — it is the only place a document transaction may be opened — and
 * impossible for a screen: RULE 4 bans `dexie` outside `data/`. So this is the
 * seam. It opens ONE transaction over exactly the tables the engine writes,
 * calls the engine, and lets a failure roll the whole thing back.
 *
 * A half-written invoice is worse than none. Allocating the number, writing the
 * header, writing the lines, posting stock and appending the audit row are eight
 * separate steps; if the seventh fails without a transaction, the user has a
 * burned invoice number, moved stock and no document.
 *
 * TWO THINGS THIS FILE DELIBERATELY DOES NOT DO
 * ----------------------------------------------
 * 1. It computes nothing. Every amount comes from `computeDocument` via
 *    `issueDocument`. A total written here would be a second calculation engine,
 *    which is the 1.6.x defect this project exists to kill (ADR 0003).
 * 2. It hardcodes no state code. `businessStateCode` decides INTRA vs INTER and
 *    comes from the BUSINESS ROW. Hardcoding '36' was true with one Telangana
 *    company and is a wrong-tax bug the moment there is a second — the same
 *    defect already fixed once on the read path.
 */
import type { DNRDatabase } from '../db/database';
import { resolveInvoicePlaceOfSupply } from '@domain/placeOfSupply';
import { issueDocument, type IssueParams } from './documentService';
import { listParties } from './partyReader';
import { listProducts, type ProductListItem } from './productReader';
import { toPartySnapshot } from './partySnapshot';
import { currentDeviceId } from '../sync/deviceIdentity';
import type { Party } from '../db/types';

/**
 * An edit to an existing DRAFT invoice.
 *
 * Deliberately does NOT extend `IssueSalesInvoiceInput`: there is no
 * `businessId`, because an edit may not move an invoice between companies, and
 * no `status`, because an edit may not issue. Making those two fields
 * unreachable by construction is worth more here than saving four lines of
 * `extends` — the whole value of this service is that it cannot be used to
 * mutate an issued document or to redirect one to another company, and a type
 * that offers both is a type someone will eventually use.
 */
export interface UpdateSalesInvoiceDraftInput {
  /** The draft to replace. Must exist and be a DRAFT. */
  readonly id: string;
  readonly customerId: string;
  readonly docDate: string;
  readonly lines: readonly InvoiceLineInput[];
  readonly roundOff?: boolean;
  readonly placeOfSupplyOverride?: string;
  /**
   * Terms for THIS draft after the edit.
   *
   * `undefined` means "re-take the profile default", which is what an unchanged
   * form should do. That is correct for a draft: a draft is not yet a historical
   * record, so it SHOULD follow the profile until it is issued. Once issued the
   * snapshot is frozen and no later edit is possible.
   */
  readonly terms?: string;
  readonly notes?: string;
  readonly deviceId?: string;
  readonly now?: string;
}

export class InvoiceServiceError extends Error {
  constructor(
    /**
     * `PLACE_OF_SUPPLY_REQUIRED` is the one the UI must handle specially: it is
     * not a failure to report, it is a question to ask. The user picks a state
     * and the operation succeeds. The other codes are all genuine refusals.
     */
    readonly code:
      | 'NO_BUSINESS'
      | 'NOT_FOUND'
      | 'NOT_EDITABLE'
      | 'PARTY_NOT_FOUND'
      | 'WRONG_BUSINESS'
      | 'NO_LINES'
      | 'NO_ITEM'
      | 'PLACE_OF_SUPPLY_REQUIRED',
    message: string,
  ) {
    super(message);
    this.name = 'InvoiceServiceError';
  }
}

/** One line as the editor collects it. Plain numbers; nothing is computed here. */
export interface InvoiceLineInput {
  readonly productId: string;
  readonly quantityMilli: number;
  readonly ratePaise: number;
  readonly description?: string;
  /** Unit of measure for this line (e.g., NOS, SQFT, KG). Defaults to product's unit. */
  readonly unit?: string;
  /**
   * Override GST rate in basis points (e.g., 1800 = 18%).
   * When provided, this is used instead of the product's tax rate.
   * Use 0 for 0% GST. Omit to fall back to product's tax rate.
   */
  readonly taxRateBp?: number;
  /**
   * Where the goods are counted from.
   *
   * Optional, and that optionality is load-bearing rather than incidental: a
   * line with no location is NOT stock-owning, so issuing the invoice posts no
   * movement. That is the correct behaviour for a service business invoicing
   * something it does not hold, and it is why a line cannot be given a
   * default location here — a silent default would deduct stock the user never
   * said was leaving the shop.
   */
  readonly stockLocationId?: string;
}

export interface IssueSalesInvoiceInput {
  /** The active business. Resolved by the caller, never assumed. */
  readonly businessId: string;
  readonly customerId: string;
  readonly docDate: string;
  readonly lines: readonly InvoiceLineInput[];
  readonly roundOff?: boolean;
  /**
   * Created as DRAFT, which is the only status `canIssue` accepts.
   *
   * Not a detail: the lifecycle deliberately forbids CREATING a document in a
   * terminal state. An invoice is drafted and then issued, and stock only moves
   * on the transition — so a service that hardcoded ISSUED would both be refused
   * by the engine and post stock for an invoice the user has not finished.
   */
  readonly status?: 'DRAFT' | 'ISSUED';
  /**
   * Where the goods are delivered to, OVERRIDING the customer master default.
   *
   * Optional per sale on purpose. A customer who normally receives at their
   * registered address and takes one consignment to a site in another state must
   * be invoiced on that state without their master record - and therefore every
   * future sale - being rewritten.
   *
   * The resolved value is frozen onto the document, so a later edit to the master
   * cannot re-tax an invoice that has already been issued.
   */
  readonly deliveryAddressLines?: readonly string[];
  /** The transaction delivery state, overriding the customer default. */
  readonly deliveryStateCode?: string;
  readonly deliveryStateName?: string;
  /**
   * An explicit place-of-supply override. Wins over every other source, and is
   * VALIDATED: an override naming a state that does not exist is reported as
   * invalid rather than quietly producing a tax split against a state code that
   * means nothing.
   */
  readonly placeOfSupplyOverride?: string;
  readonly user?: { readonly id: string; readonly name: string };
  /**
   * Per-invoice override for the printed terms.
   *
   * Optional because the DEFAULT comes from the business profile, which is what
   * a shopkeeper means: they set their terms once, and every invoice carries
   * them. This override exists for the invoice that genuinely needs different
   * terms ("delivery in 3 days" against a standing "delivery in 7 days"), and it
   * is per-sale so the standing terms are not rewritten for every later invoice.
   *
   * Whatever is used is FROZEN onto the document at creation. Later editing the
   * profile does not alter invoices already issued.
   */
  readonly terms?: string;
  /** Per-invoice notes. Same default-and-freeze rule as `terms`. */
  readonly notes?: string;
  /**
   * This install's device id.
   *
   * Optional here and RESOLVED by this service, because this is the outermost
   * seam — the one place a screen actually calls. Lower services take it as a
   * required parameter instead: they run inside a transaction the caller opened,
   * and reading the id themselves would mean putting the `settings` table in
   * every business transaction to fetch a value that never changes mid-session.
   */
  readonly deviceId?: string;
  /** Injectable clock, so the recorded timestamp is the one the caller asserts. */
  readonly now?: string;
  /**
   * Write onto an existing DRAFT row instead of inserting a new one.
   *
   * NOT part of the editor's vocabulary — `updateSalesInvoiceDraft` is the only
   * caller, and it exists so this service never grows a second public way to
   * mutate a row. It is here rather than duplicated because the transaction, the
   * validation, the calculation and the sync wiring are all here: an edit that
   * rebuilt any of them would be a second code path, which is the one thing this
   * service exists to prevent.
   *
   * The draft-status check is done by that caller, deliberately. It is the
   * service's whole value that the check is not optional.
   */
  readonly replaceIdentity?: { readonly id: string; readonly docNumber: string };
}

/** Everything the editor needs to draw itself, resolved once for the business. */
export interface InvoiceFormOptions {
  readonly customers: readonly Party[];
  readonly products: readonly ProductListItem[];
}

const DEFAULT_USER = { id: 'local', name: 'You' };

/**
 * Everything the invoice form needs for one company.
 *
 * Read ONCE and cached by the caller. This is a customer list and a product
 * list; re-running it on every keystroke is the cost that is invisible with
 * three records and ruinous with 115 parties and 921 products.
 *
 * `db` first, like every reader in this codebase, so a test can pass its own
 * database instead of the app singleton.
 */
export async function invoiceFormOptions(
  db: DNRDatabase,
  businessId: string,
): Promise<InvoiceFormOptions> {
  const [customers, products] = await Promise.all([
    listParties(db as never, businessId, { kind: 'CUSTOMER' }),
    listProducts(db as never, businessId, { activeOnly: true }),
  ]);
  return { customers, products };
}

/**
 * Issue one sales invoice, atomically.
 *
 * @throws InvoiceServiceError for every state the user can act on — no company,
 *         a customer from another company, no lines, an unknown item — rather
 *         than one generic failure, because each has a different fix.
 */
export async function issueSalesInvoice(
  db: DNRDatabase,
  input: IssueSalesInvoiceInput,
): Promise<{ id: string; docNumber: string; grandTotalPaise: number }> {
  if (input.lines.length === 0) {
    throw new InvoiceServiceError('NO_LINES', 'An invoice needs at least one line.');
  }

  // Resolved OUTSIDE the transaction and passed down. Every mutation the
  // document records must name the same originating device, and resolving it here
  // is also the only way to keep `settings` out of the invoice transaction.
  const deviceId = input.deviceId ?? (await currentDeviceId(db));
  const now = input.now ?? new Date().toISOString();

  // The customer is resolved HERE, behind the business check, so a caller cannot
  // pass an id belonging to another company. This is the write-side twin of
  // partyReader's list filter: a shared device, two companies, and an invoice
  // raised against the wrong customer's GSTIN is a real compliance failure.
  const customer = await db.parties.get(input.customerId);
  if (!customer) {
    throw new InvoiceServiceError(
      'PARTY_NOT_FOUND',
      `No customer with id "${input.customerId}". Refusing to issue an invoice to a ` +
        'customer that does not exist.',
    );
  }
  if (customer.businessId !== input.businessId) {
    throw new InvoiceServiceError(
      'WRONG_BUSINESS',
      "That customer belongs to a different company. Refusing, because raising an invoice to " +
        "another company's customer puts the wrong GSTIN on a real tax document.",
    );
  }

  const business = await db.businesses.get(input.businessId);
  if (!business) {
    throw new InvoiceServiceError(
      'NO_BUSINESS',
      'No company is selected. Choose a company before issuing an invoice.',
    );
  }

  // The tax rate comes from the INVOICE LINE when overridden, otherwise from the
  // PRODUCT. An invoice line's taxRateBp (if provided) is a deliberate override
  // for this transaction; the product's tax rate is the default for new lines.
  // Once an invoice is saved, its line-level taxRateBp is frozen and survives
  // product master changes. Use nullish coalescing (??) so 0% is preserved.
  const resolved = await Promise.all(
    input.lines.map(async (line) => {
      const product = await db.products.get(line.productId);
      if (!product) {
        throw new InvoiceServiceError(
          'NO_ITEM',
          `No item with id "${line.productId}". Refusing to sell something the catalogue ` +
            'does not contain.',
        );
      }
      if (product.businessId !== input.businessId) {
        throw new InvoiceServiceError(
          'WRONG_BUSINESS',
          "One of the chosen items belongs to a different company. Refusing, because selling " +
            "another company's stock under this letterhead puts the wrong GSTIN on a real tax " +
            'document.',
        );
      }
      const tax = product.taxRateId ? await db.taxRates.get(product.taxRateId) : undefined;
      const productTaxRateBp = tax?.rateBp ?? 0;
      return { line, product, taxRateBp: line.taxRateBp ?? productTaxRateBp };
    }),
  );

  // OWNER DECISION: the place of supply is resolved from the TRANSACTION, not
  // from `business.stateCode`.
  //
  // The supplier's state says where the business is. It is not the place of
  // supply for a sale, and using it as one charged CGST + SGST on interstate
  // invoices where IGST is due — a wrong tax document, and one that still
  // reconciles internally, so no report would ever flag it.
  //
  // `resolvePlaceOfSupply` is the single definition, shared with the editor
  // preview, so what the user is shown and what gets written cannot disagree.
  //
  // NO `reverseCharge` input: the locked decision is that reverse charge is not a
  // place-of-supply source. It is a separate question about who is the recipient.
  //
  // THE DELIVERY ADDRESS BELONGS TO THE TRANSACTION, NOT THE MASTER.
  // The customer master carries a DEFAULT, and the per-sale value overrides it.
  // That is what lets one consignment go to a site in another state without
  // rewriting where that customer "always" receives goods — and it is why the
  // resolved code below can be frozen safely.
  const deliveryStateCode = input.deliveryStateCode ?? customer.shippingStateCode;
  const deliveryStateName = input.deliveryStateName ?? customer.shippingStateName;
  const deliveryAddressLines =
    input.deliveryAddressLines ?? customer.shippingAddressLines ?? undefined;

  //
  // `resolveInvoicePlaceOfSupply` is the SAME helper the editor preview calls, so
  // the state shown on screen and the state written onto the document come from
  // one function. A second copy of these rules here would be free to drift, and
  // the drift is invisible: the screen would show CGST + SGST while the saved
  // invoice carried IGST, and both would reconcile.
  const placeOfSupply = resolveInvoicePlaceOfSupply({
    businessStateCode: business.stateCode,
    customer: {
      stateCode: customer.stateCode,
      // The per-sale delivery state, which already fell back to the master
      // default above.
      shippingStateCode: deliveryStateCode,
    },
    overrideStateCode: input.placeOfSupplyOverride,
  });

  // The locked decision, enforced where it matters: when the place of supply
  // cannot be determined from the transaction, we do NOT guess. We refuse, and we
  // say why, so the user can select the correct state.
  //
  // A draft is still allowed to be saved — you cannot forbid someone from typing
  // their notes — but it cannot be ISSUED without it, because issuing is what
  // creates the tax liability.
  if (!placeOfSupply.resolved && (input.status ?? 'DRAFT') === 'ISSUED') {
    throw new InvoiceServiceError(
      'PLACE_OF_SUPPLY_REQUIRED',
      `Place of supply is required before issuing this invoice (${placeOfSupply.reason}). ` +
        'Select the state where the supply occurs.',
    );
  }

  const params: IssueParams = {
    businessId: input.businessId,
    businessStateCode: business.stateCode,
    docType: 'SALES_INVOICE',
    docDate: input.docDate,
    customerId: customer.id,
    partySnapshot: toPartySnapshot(customer),
    // The RESOLVED value, frozen into the document. It is a snapshot, not a live
    // lookup: editing the customer's address later must not rewrite the tax on an
    // invoice that has already been issued. Empty only for a DRAFT, which cannot
    // be issued until it is filled in.
    placeOfSupplyCode: placeOfSupply.resolved ? placeOfSupply.code : '',
    // The standing terms from the business profile, unless this sale overrides
    // them. Frozen here, onto the document, so a later profile edit cannot
    // rewrite the foot of an invoice that has already been issued.
    //
    // `documentSettings` is read optionally: rows written by the 1.6.x importer
    // and by the test fixtures are sparse, and a profile edit must not become
    // the reason an invoice cannot be created.
    terms: input.terms ?? business.documentSettings?.terms,
    notes: input.notes ?? business.documentSettings?.notes,
    // The delivery address this sale used, stored so the resolution above stays
    // auditable. A frozen `placeOfSupplyCode` with no recorded delivery state
    // cannot be explained to an auditor later.
    ...(deliveryAddressLines?.length ? { deliveryAddressLines: [...deliveryAddressLines] } : {}),
    ...(deliveryStateCode ? { deliveryStateCode } : {}),
    ...(deliveryStateName ? { deliveryStateName } : {}),
    lines: resolved.map(({ line, product, taxRateBp }) => ({
      productId: product.id,
      description: line.description ?? product.name,
      hsnSac: product.hsnSac,
      unit: line.unit ?? product.unit,
      quantityMilli: line.quantityMilli,
      ratePaise: line.ratePaise,
      taxRateBp,
      cessRateBp: product.cessRateBp,
      isStockTracked: product.isStockTracked,
      // Carried through verbatim. A line with no location is not stock-owning,
      // so this is what decides whether issuing the invoice posts a movement.
      ...(line.stockLocationId ? { stockLocationId: line.stockLocationId } : {}),
    })),
    roundOff: input.roundOff ?? false,
    status: input.status ?? 'DRAFT',
    // Present only for an edit. `issueDocument` then writes onto this row instead
    // of inserting one, which is what keeps the audit row, the outbox entry and
    // the document all describing the SAME invoice.
    ...(input.replaceIdentity ? { replaceIdentity: input.replaceIdentity } : {}),
    user: input.user ?? DEFAULT_USER,
    now: now,
    // Resolved ONCE, outside the transaction, and passed in. The document's
    // mutations are recorded inside the transaction below, and every one of them
    // has to name the same originating device.
    deviceId,
  };

  // ONE transaction, over exactly the tables the engine writes. A table missing
  // from this scope is not a silent omission — Dexie refuses the write — which is
  // why the list is explicit and mirrors TxTables.
  const result = await db.transaction(
    'rw',
    [
      db.documents,
      db.documentLines,
      db.documentLinks,
      db.stockTransactions,
      db.documentSequences,
      db.auditLogs,
      db.parties,
      // Read inside the transaction for the deliver-and-invoice setting, which
      // decides whether an invoice with no Delivery Note may own the movement.
      // Omitting it aborts the write, which is the correct failure: a silent
      // fallback would post stock under a default the business never chose.
      db.businesses,
      // The outbox, so the document, its lines, its source links and its stock
      // movements are recorded in THIS transaction. Without it Dexie would abort
      // the write, which is the right failure: a committed invoice that was never
      // published is a silently divergent ledger.
      db.syncOutbox,
    ],
    () => issueDocument(db as never, params),
  );

  return {
    id: result.id,
    docNumber: result.docNumber,
    grandTotalPaise: result.grandTotalPaise,
  };
}

/**
 * Update an existing DRAFT invoice in place.
 *
 * WHY THIS EXISTS, AND WHY IT REUSES `issueDocument`
 * -------------------------------------------------
 * Without it a saved draft is a dead end: the only write path in the app was
 * `issueSalesInvoice`, which always INSERTS. A shopkeeper who saved a draft
 * halfway could not change the customer, the items or the quantities — the only
 * options were to raise it wrong or raise it twice.
 *
 * The obvious implementation is to write the header and lines by hand. That is
 * exactly the trap this project is built to avoid: the header carries ~30 money
 * fields that `computeDocument` derives and `normalizeDocument` later REFUSES to
 * render if the header and lines disagree. Hand-writing them means a second
 * calculation engine, and the symptom would be an invoice that saves cleanly and
 * then fails to preview, or worse, one that previews a total its own tax summary
 * contradicts.
 *
 * So this rebuilds through `issueSalesInvoice` — the same validation, the same
 * engine, the same transaction, the same sync wiring — passing the row's existing
 * identity so it writes ONTO the original instead of inserting beside it. There
 * is exactly one code path that can produce a sales invoice, which is the
 * property worth protecting.
 *
 * WHY "IN PLACE" MEANS THE SAME ROW, NOT A REPLACEMENT
 * ---------------------------------------------------
 * The first version of this built a THROWAWAY document and then moved it onto the
 * original. Every local test passed, and the local database always came out
 * right. It was still wrong, because `issueDocument` does two things beyond
 * writing the document: it writes an audit row, and it pushes a sync mutation —
 * both keyed on the id it was given. Move the header afterwards and the audit
 * trail and the outbox both describe an invoice that no longer exists, while the
 * invoice that DOES exist is never published. The user's other device would gain
 * a ghost invoice they cannot open, and would keep showing the pre-edit draft
 * forever, with no error on either device.
 *
 * So the identity is carried INTO the build instead. One row, one id, one audit
 * entry, one outbox mutation, all in one transaction. There is no window in
 * which they can disagree, because there is no moment at which they are not the
 * same write.
 *
 * WHAT IT REFUSES
 * ---------------
 * Only a DRAFT. An issued invoice is a tax document: its number is in the
 * customer's books and in the GSTR-1 return. Editing one in place would silently
 * rewrite history, so the correction is the existing cancellation lifecycle plus
 * a fresh invoice. This check is the write-side twin of the lifecycle rule, and
 * it is deliberately not `allowEditIssuedDocuments`: a business that ticked that
 * preference would still not be able to mutate an issued tax document.
 *
 * @throws InvoiceServiceError `NOT_EDITABLE` for an issued/cancelled document,
 *         `NOT_FOUND` when the id is unknown.
 */
export async function updateSalesInvoiceDraft(
  db: DNRDatabase,
  input: UpdateSalesInvoiceDraftInput,
): Promise<{ id: string; docNumber: string; grandTotalPaise: number }> {
  const existing = await db.documents.get(input.id);
  if (!existing) {
    throw new InvoiceServiceError(
      'NOT_FOUND',
      'That invoice no longer exists. It may have been deleted.',
    );
  }
  if (existing.status !== 'DRAFT') {
    // Named explicitly rather than folded into a generic error: the user's next
    // action differs completely, and "it is already issued" is a dead end they
    // must be told about plainly rather than discover on the next click.
    throw new InvoiceServiceError(
      'NOT_EDITABLE',
      `Invoice ${existing.docNumber} is already ${existing.status.toLowerCase()}, so it ` +
        'cannot be edited. Cancel it and raise a new one — an issued tax document is a ' +
        'historical record and changing it would alter what the customer already holds.',
    );
  }
  if (input.lines.length === 0) {
    throw new InvoiceServiceError('NO_LINES', 'An invoice needs at least one line.');
  }

  // Everything that decides a price, a tax rate, a stock movement or a place of
  // supply is `issueSalesInvoice`'s code, unchanged. This path differs from
  // creation in exactly two ways: it writes onto the existing row, and it keeps
  // that row's invoice number. Both are settled below.
  const built = await issueSalesInvoice(db, {
    businessId: existing.businessId,
    customerId: input.customerId,
    docDate: input.docDate,
    lines: input.lines,
    roundOff: input.roundOff ?? false,
    terms: input.terms,
    notes: input.notes,
    placeOfSupplyOverride: input.placeOfSupplyOverride,
    // Stays a draft. Editing must never become a back door to issuing.
    status: 'DRAFT',
    // The one difference from creation. `issueDocument` overwrites this row
    // rather than inserting a new one, so the id the user edited, the number
    // they were shown, the audit entry and the sync mutation all refer to the
    // same invoice — with no temporary document ever existing.
    replaceIdentity: { id: existing.id, docNumber: existing.docNumber },
    deviceId: input.deviceId,
    now: input.now,
  });

  return {
    id: built.id,
    docNumber: built.docNumber,
    grandTotalPaise: built.grandTotalPaise,
  };
}